import { formatMoneyIntl, dateLocale, timeLocale } from './format-locale';
/**
 * Common formatting utilities
 */

/**
 * Format date to locale string
 */
export function formatDate(
  date: Date | string | undefined | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  if (Object.keys(options).length > 0) {
    return dateObj.toLocaleDateString(dateLocale(), options);
  }

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format a date+time as "dd/mm/yyyy h:mm AM/PM" — used in tooltips and
 * compact strips where we want the user-locale 12-hour clock alongside the
 * day-first date. Returns an empty string for missing or invalid input.
 *
 *   formatDateTime12('2026-05-31T12:17:00Z') → '31/05/2026 12:17 PM'
 */
export function formatDateTime12(
  date: Date | string | null | undefined,
): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  // The clock goes through `Intl` so the AM/PM marker is in the app language.
  const clock = d.toLocaleTimeString(timeLocale(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${day}/${month}/${year} ${clock}`;
}

/**
 * Format time only
 */
export function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  // A wall-clock "HH:mm" has no zone: format it as UTC so it is not shifted.
  const clock = new Date(
    Date.UTC(2000, 0, 1, parseInt(hours, 10), parseInt(minutes, 10)),
  );
  if (isNaN(clock.getTime())) return time;
  return clock.toLocaleTimeString(timeLocale(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
}

/**
 * Format time from Date object.
 *
 * Pass `options.timeZone` (an IANA zone) to render the clinic's wall-clock
 * instead of the browser's local zone — use the clinic timezone from
 * useClinicTimezone() for appointment times so all users see the same clock.
 */
export function formatTimeFromDate(
  date: Date | string | undefined | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };

  return dateObj.toLocaleTimeString(timeLocale(), defaultOptions);
}

/**
 * Capitalize first letter
 */
export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format a boolean value to Yes/No/Not set
 */
export function formatBoolValue(value?: boolean | null): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not set';
}

/**
 * Format currency value
 */
export function formatCurrency(
  value: number | string | undefined | null,
  options: {
    locale?: string;
    currency?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  // Delegates to the one formatter: active language, scope currency, the
  // currency's own decimals. (It used to be hardcoded 'en-US' and 'EGP'.)
  return formatMoneyIntl(
    numValue === undefined || numValue === null || isNaN(numValue)
      ? 0
      : numValue,
    {
      ...(options.locale ? { locale: options.locale } : {}),
      ...(options.currency ? { currency: options.currency } : {}),
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      ...(options.maximumFractionDigits !== undefined
        ? { maximumFractionDigits: options.maximumFractionDigits }
        : {}),
      bidiIsolate: false,
    },
  );
}

/**
 * Format number with thousands separator
 */
export function formatNumber(
  value: number | string | undefined | null,
  options: {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string {
  const {
    locale = 'en-US',
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  } = options;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numValue === undefined || numValue === null || isNaN(numValue)) {
    return '0';
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(numValue);
}

/**
 * Format date and time
 */
export function formatDateTime(
  date: Date | string | undefined | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };
  const time = dateObj.toLocaleTimeString(timeLocale(), timeOptions);
  return `${day}/${month}/${year}, ${time}`;
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(
  date: Date | string | undefined | null,
): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  // `Intl` owns the wording and the plural forms, in the app language.
  const relative = new Intl.RelativeTimeFormat(dateLocale(), {
    numeric: 'auto',
  });
  if (diffSec < 60) return relative.format(0, 'second');
  if (diffMin < 60) return relative.format(-diffMin, 'minute');
  if (diffHour < 24) return relative.format(-diffHour, 'hour');
  if (diffDay < 7) return relative.format(-diffDay, 'day');

  return formatDate(dateObj, { month: 'short' });
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(
  text: string | undefined | null,
  maxLength: number,
): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string | undefined | null): string {
  if (!phone) return '';

  // Remove non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length === 11) {
    // Egyptian mobile format: 01X XXXX XXXX
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`;
  }

  if (cleaned.length === 10) {
    // Standard format: XXX XXX XXXX
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }

  return phone;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * A file size kept in one piece inside a sentence: without the isolate,
 * Arabic text reorders "3 MB of 1 MB" into "MB 3 من MB 1".
 */
export function formatFileSizeInText(bytes: number): string {
  return `\u2068${formatFileSize(bytes)}\u2069`;
}

/**
 * Format percentage
 */
export function formatPercentage(
  value: number | undefined | null,
  options: { decimals?: number; includeSign?: boolean } = {},
): string {
  const { decimals = 1, includeSign = true } = options;

  if (value === undefined || value === null || isNaN(value)) {
    return '0%';
  }

  const formatted = value.toFixed(decimals);
  return includeSign ? `${formatted}%` : formatted;
}

/**
 * Convert snake_case or kebab-case to Title Case
 */
export function toTitleCase(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .replace(/[-_]/g, ' ')
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase(),
    );
}

/**
 * Initials for an avatar — first letters of the first two words.
 *
 *   getInitials('Sarah Mahmoud') → 'SM'
 */
export function getInitials(name: string | undefined | null): string {
  if (!name) return '?';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return initials || '?';
}

/**
 * Age in whole years, accounting for whether this year's birthday has passed.
 * Returns null for missing or invalid input.
 */
export function calculateAge(
  birthDate: string | Date | undefined | null,
): number | null {
  if (!birthDate) return null;
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Deterministic avatar tint, so a given name always gets the same colour.
 * Returns Tailwind background + text classes from the design-system palette.
 */
const AVATAR_COLORS = [
  'bg-[var(--identity-1-bg)] text-[var(--identity-1-fg)]',
  'bg-[var(--identity-2-bg)] text-[var(--identity-2-fg)]',
  'bg-[var(--identity-3-bg)] text-[var(--identity-3-fg)]',
  'bg-[var(--identity-4-bg)] text-[var(--identity-4-fg)]',
  'bg-[var(--identity-5-bg)] text-[var(--identity-5-fg)]',
  'bg-[var(--identity-6-bg)] text-[var(--identity-6-fg)]',
];

export function getAvatarColor(name: string | undefined | null): string {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
